import { nanoid } from "nanoid";
import { z } from "zod";

export const numericInputSchema = z.string().min(1).max(160);
export type NumericInput = z.infer<typeof numericInputSchema>;

const vector3Schema = z.tuple([numericInputSchema, numericInputSchema, numericInputSchema]);
export type ExpressionVector3 = z.infer<typeof vector3Schema>;

export const parameterSchema = z.object({
  id: z.string(),
  name: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/),
  label: z.string(),
  type: z.enum(["length", "angle", "integer"]),
  value: numericInputSchema,
  minimum: z.number().optional(),
  maximum: z.number().optional(),
  step: z.number().positive().optional()
});
export type ParameterDefinition = z.infer<typeof parameterSchema>;

export const exposedParameterSchema = z.object({
  parameterId: z.string(),
  label: z.string(),
  group: z.string().default("Dimensions"),
  minimum: z.number().optional(),
  maximum: z.number().optional(),
  step: z.number().positive().default(1)
});
export type ExposedParameter = z.infer<typeof exposedParameterSchema>;

const nodeBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean().default(true),
  visible: z.boolean().default(true),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#73d8b4"),
  inputNodeIds: z.array(z.string()).default([])
});

const primitivePlacementSchema = z.object({
  position: vector3Schema.default(["0", "0", "0"]),
  rotation: vector3Schema.default(["0", "0", "0"])
});

export const boxNodeSchema = nodeBaseSchema.extend({
  type: z.literal("box"),
  width: numericInputSchema,
  depth: numericInputSchema,
  height: numericInputSchema,
  centered: z.boolean().default(true),
  placement: primitivePlacementSchema
});

export const cylinderNodeSchema = nodeBaseSchema.extend({
  type: z.literal("cylinder"),
  radius: numericInputSchema,
  height: numericInputSchema,
  placement: primitivePlacementSchema
});

export const sphereNodeSchema = nodeBaseSchema.extend({
  type: z.literal("sphere"),
  radius: numericInputSchema,
  placement: primitivePlacementSchema
});

export const coneNodeSchema = nodeBaseSchema.extend({
  type: z.literal("cone"),
  bottomRadius: numericInputSchema,
  topRadius: numericInputSchema,
  height: numericInputSchema,
  placement: primitivePlacementSchema
});

export const wedgeNodeSchema = nodeBaseSchema.extend({
  type: z.literal("wedge"),
  width: numericInputSchema,
  depth: numericInputSchema,
  height: numericInputSchema,
  placement: primitivePlacementSchema
});

export const transformNodeSchema = nodeBaseSchema.extend({
  type: z.literal("transform"),
  translation: vector3Schema,
  rotation: vector3Schema,
  scale: numericInputSchema.default("1")
});

export const booleanNodeSchema = nodeBaseSchema.extend({
  type: z.literal("boolean"),
  operation: z.enum(["union", "subtract", "intersect"])
});

export const holeNodeSchema = nodeBaseSchema.extend({
  type: z.literal("hole"),
  radius: numericInputSchema,
  depth: numericInputSchema,
  position: vector3Schema,
  axis: z.enum(["X", "Y", "Z"]).default("Z")
});

export const filletNodeSchema = nodeBaseSchema.extend({
  type: z.literal("fillet"),
  radius: numericInputSchema,
  selection: z.enum(["all", "vertical", "horizontal"]).default("vertical")
});

export const chamferNodeSchema = nodeBaseSchema.extend({
  type: z.literal("chamfer"),
  distance: numericInputSchema,
  selection: z.enum(["all", "vertical", "horizontal"]).default("vertical")
});

export const mirrorNodeSchema = nodeBaseSchema.extend({
  type: z.literal("mirror"),
  plane: z.enum(["XY", "XZ", "YZ"]).default("YZ"),
  keepOriginal: z.boolean().default(true),
  fuseCopies: z.boolean().default(false)
});

export const linearPatternNodeSchema = nodeBaseSchema.extend({
  type: z.literal("linearPattern"),
  count: numericInputSchema,
  spacing: numericInputSchema,
  direction: z.enum(["X", "Y", "Z"]).default("X"),
  fuseCopies: z.boolean().default(false)
});

export const circularPatternNodeSchema = nodeBaseSchema.extend({
  type: z.literal("circularPattern"),
  count: numericInputSchema,
  totalAngle: numericInputSchema,
  axis: z.enum(["X", "Y", "Z"]).default("Z"),
  origin: vector3Schema.default(["0", "0", "0"]),
  fuseCopies: z.boolean().default(false)
});

export const featureNodeSchema = z.discriminatedUnion("type", [
  boxNodeSchema,
  cylinderNodeSchema,
  sphereNodeSchema,
  coneNodeSchema,
  wedgeNodeSchema,
  transformNodeSchema,
  booleanNodeSchema,
  holeNodeSchema,
  filletNodeSchema,
  chamferNodeSchema,
  mirrorNodeSchema,
  linearPatternNodeSchema,
  circularPatternNodeSchema
]);

export type FeatureNode = z.infer<typeof featureNodeSchema>;
export type NodeType = FeatureNode["type"];

export const motiveDocumentSchema = z.object({
  schemaVersion: z.literal(1),
  applicationVersion: z.string(),
  id: z.string(),
  name: z.string().min(1).max(120),
  units: z.literal("mm"),
  parameters: z.record(z.string(), parameterSchema),
  nodes: z.array(featureNodeSchema),
  exposedParameters: z.array(exposedParameterSchema),
  viewport: z.object({
    projection: z.enum(["perspective", "orthographic"]).default("perspective"),
    showGrid: z.boolean().default(true),
    showEdges: z.boolean().default(true),
    background: z.string().default("#16191f")
  }),
  metadata: z.object({
    createdAt: z.string(),
    modifiedAt: z.string(),
    description: z.string().optional()
  })
});

export type MotiveDocument = z.infer<typeof motiveDocumentSchema>;

export type NodeDiagnostic = {
  nodeId: string;
  severity: "warning" | "error";
  code: string;
  message: string;
};

export type MeshBody = {
  nodeId: string;
  name: string;
  color: string;
  vertices: Float32Array;
  normals: Float32Array;
  triangles: Uint32Array;
  lines: Float32Array;
  faceGroups: Array<{ start: number; count: number; faceId: number }>;
  edgeGroups: Array<{ start: number; count: number; edgeId: number }>;
  bounds: { min: [number, number, number]; max: [number, number, number] };
  volume: number;
  area: number;
};

export type EvaluationResult = {
  revision: number;
  bodies: MeshBody[];
  diagnostics: NodeDiagnostic[];
  durationMs: number;
};

export const APP_VERSION = "0.1.0";

const nowIso = () => new Date().toISOString();

export function makeEmptyDocument(name = "Untitled model"): MotiveDocument {
  const now = nowIso();
  return {
    schemaVersion: 1,
    applicationVersion: APP_VERSION,
    id: nanoid(),
    name,
    units: "mm",
    parameters: {},
    nodes: [],
    exposedParameters: [],
    viewport: {
      projection: "perspective",
      showGrid: true,
      showEdges: true,
      background: "#16191f"
    },
    metadata: { createdAt: now, modifiedAt: now }
  };
}

const baseNode = (name: string): Omit<z.infer<typeof nodeBaseSchema>, "inputNodeIds"> & { inputNodeIds: string[] } => ({
  id: nanoid(),
  name,
  enabled: true,
  visible: true,
  color: "#73d8b4",
  inputNodeIds: []
});

const placement = (offset = 0): z.infer<typeof primitivePlacementSchema> => ({
  position: [String(offset), "0", "0"],
  rotation: ["0", "0", "0"]
});

export function createPrimitiveNode(type: "box" | "cylinder" | "sphere" | "cone" | "wedge", offset = 0): FeatureNode {
  switch (type) {
    case "box":
      return { ...baseNode("Box"), type, width: "60", depth: "40", height: "20", centered: true, placement: placement(offset) };
    case "cylinder":
      return { ...baseNode("Cylinder"), type, radius: "15", height: "30", placement: placement(offset) };
    case "sphere":
      return { ...baseNode("Sphere"), type, radius: "20", placement: placement(offset) };
    case "cone":
      return { ...baseNode("Cone"), type, bottomRadius: "20", topRadius: "5", height: "35", placement: placement(offset) };
    case "wedge":
      return { ...baseNode("Wedge"), type, width: "50", depth: "35", height: "25", placement: placement(offset) };
  }
}

export function touchDocument(document: MotiveDocument): void {
  document.metadata.modifiedAt = nowIso();
  document.applicationVersion = APP_VERSION;
}
